// The Invoices page's reads and read rules: ONE copy, used by the desktop Invoices page
// (pages/InvoicesModule.jsx) and the read-only phone page (mobile/screens/InvoicesScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page;
// pages/InvoicesModule.reads.actions.test.jsx pins the desktop's reads.
//
// axios like the page (its X-API-Key interceptor), with the base URL passed on each request: the page
// sets axios.defaults.baseURL when it loads, and a phone screen must not depend on that.
import axios from 'axios';
import config from '../../config.js';
import { invoiceAssemblyPricing } from '../../utils/salesOrderMath';
import { fmt } from '../../utils/financeFormat';
import { bookedRevenueSplit } from '../../utils/tmPricing';

const API = { baseURL: config.API_BASE_URL };

// --- The list --------------------------------------------------------------------------------------

/** The list's status filter, as the desktop offers it ("Overdue" = Open past its due date, on the API). */
export const INVOICE_STATUS_OPTIONS = Object.freeze([
  { value: '', label: '(All)' },
  { value: 'Open', label: 'Open' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Overdue', label: 'Overdue' },
  { value: 'Void', label: 'Void' },
]);

/** GET /invoices params: a search spans every status (customer / dates still narrow it); else the status. */
export function invoiceListParams({ search, status, invoiceNumber, startDate, endDate, customerId } = {}) {
  const params = {};
  const q = String(search || '').trim();
  if (q) {
    // Universal reference search — matches invoice #, customer PO, customer SO, our SO# (SO-####),
    // master job #, and customer name.
    params.search = q;
  } else if (status) {
    params.status = status;
  }
  if (invoiceNumber) params.invoice_number = invoiceNumber;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (customerId) params.customer_id = customerId;
  return params;
}

/** GET /invoices (invoice_id newest first). Throws. */
export async function loadInvoices(filters) {
  const res = await axios.get('/invoices', { params: invoiceListParams(filters), ...API });
  return res.data;
}

/** The customers in a list, A-Z (the desktop's customer filter). */
export function customersFromInvoices(list) {
  const out = [];
  const seen = new Map();
  (list || []).forEach((inv) => {
    if (inv.customer_id && !seen.has(inv.customer_id)) {
      seen.set(inv.customer_id, true);
      out.push({ id: inv.customer_id, name: inv.customer_name });
    }
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Days an Open invoice is past its due date: due dates are calendar days, so one due today is not
 *  past due until tomorrow. `today` = YYYY-MM-DD (utils/shopDate.shopToday). 0 = not past due. */
export function daysPastDue(inv, today) {
  if (!inv || inv.status !== 'Open' || !inv.due_date) return 0;
  const due = String(inv.due_date).slice(0, 10);
  if (!(due < today)) return 0;
  const [y1, m1, d1] = due.split('-').map(Number);
  const [y2, m2, d2] = today.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
}

/** What a list row shows besides its number and customer. */
export function invoiceRowInfo(inv) {
  const poLabel = inv.customer_po ? ` | PO: ${inv.customer_po}` : '';
  const custSoLabel = inv.customer_so ? ` | Cust SO: ${inv.customer_so}` : '';
  const soLabel = inv.order_number ? ` | SO: ${inv.order_number}` : '';
  const totalPaid = parseFloat(inv.total_paid || 0);
  const invTotal = parseFloat(inv.total || 0);
  const retainage = parseFloat(inv.retainage_held_amount || 0);
  const retainageOpen = retainage > 0 && !inv.retainage_released_at;
  return {
    secondaryText: `$${(inv.total || 0).toFixed(2)} | ${inv.status}${poLabel}${custSoLabel}${soLabel}`,
    totalPaid,
    invTotal,
    retainage,
    retainageOpen,
    isPartiallyPaid: totalPaid > 0.01 && totalPaid < invTotal - 0.01 && inv.status === 'Open',
  };
}

// --- One invoice ---------------------------------------------------------------------------------------

/** GET /invoices/{id}: header, lines, payments, chain_alignment, so_documents. Throws. */
export async function loadInvoice(invoiceId) {
  const res = await axios.get(`/invoices/${invoiceId}`, API);
  return res.data;
}

/** The emails sent with the invoice (its "Emailed…" snapshots). Throws. */
export async function loadInvoiceEmails(invoiceId) {
  const res = await axios.get(`/invoices/${invoiceId}/snapshots`, API);
  return (res.data || []).filter((s) => s.action_type && s.action_type.startsWith('Emailed'));
}

/** GET /invoices/{id}/payment-inquiries, newest first. Throws. */
export async function loadPaymentInquiries(invoiceId) {
  const res = await axios.get(`/invoices/${invoiceId}/payment-inquiries`, API);
  return res.data || [];
}

/** An inquiry's line for this invoice ({balance_at_send, pdf_url, ...}; {} when it isn't on it). */
export function inquiryForInvoice(inquiry, invoiceId) {
  return ((inquiry && inquiry.invoices) || []).find((x) => x.invoice_id === invoiceId) || {};
}

/** The balance an inquiry asked about, as money ("—" when the inquiry carries none). fmt adds the
 *  "$" (the page printed "$$1,759.34" until 2026-10-14). */
export function inquiryBalanceAsked(mine) {
  return mine.balance_at_send != null ? fmt(mine.balance_at_send) : '—';
}

/** The status as the office reads it: "Partially Paid" (or "… (Retainage Held)") for an Open invoice
 *  with a payment on it; the DB status is unchanged. {display, chipColor} (MUI colors). */
export function invoiceStatusDisplay(inv) {
  const status = inv.status;
  const total = parseFloat(inv.total || 0);
  const paid = parseFloat(inv.total_paid || 0);
  const retainageOpen = parseFloat(inv.retainage_held_amount || 0) > 0 && !inv.retainage_released_at;
  let display = status;
  let chipColor = 'default';
  if (status === 'Open' && paid > 0.01 && paid < total - 0.01) {
    display = retainageOpen ? 'Partially Paid (Retainage Held)' : 'Partially Paid';
    chipColor = retainageOpen ? 'warning' : 'info';
  } else if (status === 'Paid') {
    chipColor = 'success';
  } else if (status === 'Void') {
    chipColor = 'error';
  }
  return { display, chipColor };
}

/** Money on an invoice: paid, the balance (never below 0), whether it is past due (calendar days;
 *  `today` = YYYY-MM-DD) and whether the balance is only the held retainage. */
export function invoiceBalance(inv, today) {
  const total = parseFloat(inv.total || 0);
  const paid = parseFloat(inv.total_paid || 0);
  const retainage = parseFloat(inv.retainage_held_amount || 0);
  const retainageOpen = retainage > 0 && !inv.retainage_released_at;
  const balance = Math.max(0, total - paid);
  const pastDue = !!inv.due_date && String(inv.due_date).slice(0, 10) < today;
  // Balance equals retainage means the only thing outstanding is retainage.
  const balanceIsAllRetainage = retainageOpen && Math.abs(balance - retainage) < 0.01;
  return { total, paid, retainage, retainageOpen, balance, pastDue, balanceIsAllRetainage };
}

/** Quote ↔ SO ↔ invoice alignment, by chain_alignment.status. */
export function chainAlignmentMeta(status) {
  return {
    severity: { aligned: 'success', partial: 'info', drift: 'error', no_quote: 'warning' }[status] || 'info',
    title: {
      aligned: 'Fully aligned — Quote, Sales Order, and Invoice reconcile',
      partial: 'Partial billing — this invoice bills a subset of the Sales Order',
      drift: 'Drift detected — review the flags below',
      no_quote: 'No source Quote linked to this Sales Order',
    }[status] || 'Chain alignment',
    chipColor: { aligned: 'success', partial: 'info', drift: 'error', no_quote: 'default' }[status] || 'default',
  };
}

/** A sales order document that is the customer's PO. */
export function isCustomerPoDoc(doc) {
  const t = (doc.doc_type || '').toLowerCase();
  return t.includes('po') || t === 'customer po';
}

/** The invoice's lines as the office reads them: an assembly's component lines fold into ONE row
 *  ("ASSEMBLY", sets = the primary part's quantity / its count per set, priced per set from the
 *  order's quoted prices, or from `truthGroups` (the SO's /truth groups) when they are loaded for
 *  this invoice's SO); plain lines follow as they are. */
export function groupInvoiceLines(inv, truthGroups = null) {
  if (!inv?.lines) return [];
  const individual = inv.lines.filter((l) => !l.source_assembly_id);
  const assemblyLines = inv.lines.filter((l) => l.source_assembly_id);

  const assemblyGroups = {};
  assemblyLines.forEach((l) => {
    if (!assemblyGroups[l.source_assembly_id]) {
      assemblyGroups[l.source_assembly_id] = {
        source_assembly_id: l.source_assembly_id,
        assembly_name: l.assembly_name,
        primary_part_id: l.primary_part_id,
        primary_part_quantity: l.primary_part_quantity || 1,
        components: [],
      };
    }
    assemblyGroups[l.source_assembly_id].components.push(l);
  });

  const grouped = Object.values(assemblyGroups).map((group) => {
    const { components, primary_part_id, primary_part_quantity, assembly_name, source_assembly_id } = group;

    // Deduplicate by sales_order_line_id so split lines (same part across multiple jobs) don't
    // double-count the per-assembly price.
    const seenSoLines = new Set();
    const assemblyUnitPriceLocal = components.reduce((sum, c) => {
      const solId = c.sales_order_line_id;
      if (solId && seenSoLines.has(solId)) return sum;
      if (solId) seenSoLines.add(solId);
      const price = c.so_unit_price != null ? c.so_unit_price : c.unit_price;
      const qtyPer = c.quantity_per_assembly || 1;
      return sum + (price * qtyPer);
    }, 0);
    // F19: bill the ORDER's per-set price when the SO's /truth groups are loaded (and belong to this
    // invoice's SO); else the local sum above. The INVOICED set count below is kept as-is — a partial
    // invoice bills fewer sets than the order carries.
    const truthGroupsForSo = truthGroups?.sales_order_id != null
      && truthGroups.sales_order_id === inv.sales_order_id
      ? truthGroups.groups
      : null;
    const perSet = invoiceAssemblyPricing({
      truthGroup: truthGroupsForSo?.[String(source_assembly_id)],
      localPerSetPrice: assemblyUnitPriceLocal,
      orderNumber: inv.order_number,
    });

    // The primary part can be split across MULTIPLE invoice lines (one per producing job). Sum them
    // all; taking only the first line under-counts the assembly qty.
    const primaryLines = components.filter((c) => c.part_id === primary_part_id);
    const primaryLine = primaryLines[0];
    const primaryQty = primaryLines.reduce((s, c) => s + (c.quantity || 0), 0);
    const displayQty = primaryLines.length ? Math.floor(primaryQty / primary_part_quantity) : 1;
    const jobNumbers = [...new Set(primaryLines.map((c) => c.master_job_number || c.job_id).filter(Boolean))];

    return {
      invoice_line_id: `asm-${components[0].invoice_line_id}`,
      product_id: 'ASSEMBLY',
      description: assembly_name || 'Assembly',
      quantity: displayQty,
      unit_price: perSet.per_set_price,
      _per_set_source: perSet,
      customer_po: primaryLine?.customer_po || components[0]?.customer_po,
      customer_so: primaryLine?.customer_so || components[0]?.customer_so,
      master_job_number: jobNumbers.join(', ') || primaryLine?.job_id,
      job_id: primaryLine?.job_id || components[0]?.job_id,
    };
  });

  return [...grouped, ...individual];
}

// --- The AR dashboard ------------------------------------------------------------------------------------

/** GET /invoices with no filter: every invoice, every status (the dashboard's numbers). Throws. */
export async function loadAllInvoices() {
  const res = await axios.get('/invoices', API);
  return res.data;
}

/** The AR numbers: outstanding (Σ total − paid over Open), overdue (calendar days past due), DSO
 *  ((AR / last 90 days invoiced) × 90), aging buckets, per-customer aging, the overdue invoices.
 *  `today` = YYYY-MM-DD. null for no invoices. */
export function arSummary(allInvoices, today) {
  if (!allInvoices || !allInvoices.length) return null;
  const openInvoices = allInvoices.filter((i) => i.status === 'Open');
  const balanceOf = (i) => (i.total || 0) - (i.total_paid || 0);

  const totalAR = openInvoices.reduce((sum, i) => sum + balanceOf(i), 0);
  const overdueInvoices = openInvoices.filter((i) => daysPastDue(i, today) > 0);
  const totalOverdue = overdueInvoices.reduce((sum, i) => sum + balanceOf(i), 0);

  // DSO = (Total AR / Total invoiced in the last 90 days) * 90: invoice dates from today − 89 through
  // today (90 calendar days, as the page has always counted them).
  const [y, m, d] = today.split('-').map(Number);
  const windowStart = new Date(Date.UTC(y, m - 1, d - 89)).toISOString().slice(0, 10);
  const revenue90 = allInvoices.reduce((sum, i) => {
    const dt = i.invoice_date ? String(i.invoice_date).slice(0, 10) : null;
    return dt && dt >= windowStart ? sum + (i.total || 0) : sum;
  }, 0);
  const dso = revenue90 > 0 ? Math.round((totalAR / revenue90) * 90) : 0;

  const bucketOf = (days) => (days <= 0 ? 'current' : days <= 30 ? 'd1_30' : days <= 60 ? 'd31_60' : days <= 90 ? 'd61_90' : 'd90plus');
  const agingBuckets = { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90plus: 0 };
  const customerAging = {};
  openInvoices.forEach((inv) => {
    const balance = balanceOf(inv);
    const bucket = bucketOf(daysPastDue(inv, today));
    agingBuckets[bucket] += balance;
    const name = inv.customer_name || 'Unknown';
    if (!customerAging[name]) customerAging[name] = { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90plus: 0, total: 0 };
    customerAging[name].total += balance;
    customerAging[name][bucket] += balance;
  });
  const agingRows = Object.entries(customerAging)
    .map(([customer, buckets]) => ({ customer, ...buckets }))
    .filter((r) => r.total > 0.01);

  const overdueDetail = overdueInvoices.map((inv) => ({
    invoice_id: inv.invoice_id,
    invoice_number: inv.invoice_number,
    customer_name: inv.customer_name,
    due_date: inv.due_date,
    total: inv.total || 0,
    balance: balanceOf(inv),
    days_overdue: daysPastDue(inv, today),
    last_emailed_at: inv.last_emailed_at,
    last_inquiry_at: inv.last_inquiry_at || null,
    inquiry_count: inv.inquiry_count || 0,
  }));

  return {
    totalAR, totalOverdue, overdueCount: overdueInvoices.length, dso, openCount: openInvoices.length,
    agingBuckets, agingRows, overdueDetail,
  };
}

/** Booked revenue not yet invoiced: uninvoiced commitment over open SOs (status=Open already excludes
 *  Completed/Cancelled). T&M orders still waiting on pricing are left out (their commitment is only
 *  shipping + fees until priced) and counted apart. {total, count, tmPendingCount, tmPendingEst}.
 *  Throws. */
export async function loadBookedRevenue() {
  const soRes = await axios.get('/sales_orders', { params: { status: 'Open' }, ...API });
  return bookedRevenueSplit(soRes.data);
}

/** DSO's word, as the desktop card says it. */
export const dsoLabel = (dso) => (dso <= 30 ? 'Healthy' : dso <= 45 ? 'Moderate' : 'Needs attention');
